import React, { useState } from 'react'
import ReactSelect, { Option } from 'react-select'

import i18n from 'live/services/i18n'

import { severityMock } from 'plugin-codaf-risk-ext-bowtie/BowtieAlarms/mocks/mocks'
import {
    IOverviewAlarmEvent,
    IOverviewAsset
} from 'plugin-codaf-risk-ext-bowtie/BowtieManagment/components/AssetSelector/AssetSelector'
import { AlarmCard } from 'plugin-codaf-risk-ext-bowtie/BowtieManagment/components/AssetSelector/components/AlarmList/components/AlarmCard/AlarmCard'
import { FilteredSearch } from 'plugin-codaf-risk-ext-bowtie/generic'
import { IBowtie } from 'plugin-codaf-risk-ext-bowtie/types'
import { IAlarmEvent } from 'plugin-codaf-risk-ext-bowtie/types/alarms'

import { CardList, DashboardAlarmListContainer } from './styles'

interface IOperationData {
    id: number
    description: string
}
interface ISeverityData {
    id: number
    name: string
}

interface IOrder {
    order: boolean
}

interface IProps {
    asset: IOverviewAsset
    alarms: Map<number, IAlarmEvent>
    setAlarmData: (k: IAlarmEvent) => void
    setSelectedBowtie: (k: number) => void
    alarmBowties: IBowtie[]
}

export const DashboardAlarmList: React.FC<IProps> = ({
    asset,
    alarms,
    setAlarmData,
    setSelectedBowtie,
    alarmBowties
}) => {
    const [showSelectDropdown, setShowSelectDropdown] = useState<boolean>(false)
    const [filterOperation, setFilterOperation] = useState<IOperationData[]>([])
    const [severityFilter, setSeverityFilter] = useState<ISeverityData[]>([])
    const [filterSeverityAlarms, setFilterSeverityAlarms] = useState<Set<string>>(new Set<string>())
    const [filterOperationAlarms, setFilterOperationAlarms] = useState<Set<number>>(new Set<number>())
    const [stringFilter, setStringFilter] = useState<string>('')

    const [order, setOrder] = useState<IOrder>({ order: true })

    const handleAlarmData = (alarm: IOverviewAlarmEvent): void => {
        if (alarm) {
            const alarmToAtt = alarms?.get(alarm.id)
            setAlarmData({
                ...alarmToAtt,
                entityName: alarm.entityName
            })
        }
    }

    return (
        <DashboardAlarmListContainer>
            <div className="filter-row">
                <FilteredSearch onSearch={setStringFilter} />
                <div
                    className="filter-button"
                    onClick={() => {
                        setShowSelectDropdown(old => !old)
                    }}
                >
                    <div className="icon-label-bundle">
                        <i className="far fa-filter" />
                        {i18n('Filter')}
                    </div>
                    {showSelectDropdown ? <i className="fas fa-caret-up" /> : <i className="fas fa-caret-down" />}
                </div>
                <div
                    className="order-button"
                    onClick={() => {
                        setOrder({
                            order: !order.order
                        })
                        setShowSelectDropdown(false)
                    }}
                >
                    <i className="fal fa-sort-alt" />
                </div>
            </div>
            <div className="list-filter second-row">
                <span className="selected-filter">{i18n('Selected Filters: ')}</span>
                <div className={'tags-row'}>
                    {filterOperation?.map((operation, index) => (
                        <div key={index} className={'selected-tag'}>
                            {operation.description}
                            <i
                                className="fas fa-times"
                                onClick={() => {
                                    setFilterOperation(filterOperation.filter(option => option.id !== operation.id))
                                    filterOperationAlarms.delete(operation.id)
                                }}
                            />
                        </div>
                    ))}
                    {severityFilter?.map((severity, index) => (
                        <div key={index} className={'selected-tag'}>
                            {i18n(severity.name)}
                            <i
                                className="fas fa-times"
                                onClick={() => {
                                    setSeverityFilter(severityFilter.filter(option => option.id !== severity.id))
                                    filterSeverityAlarms.delete(severity.name.toUpperCase())
                                }}
                            />
                        </div>
                    ))}
                </div>
                <div className="clear">
                    <span
                        className={'clear-label'}
                        onClick={() => {
                            setSeverityFilter([])
                            setFilterOperation([])
                            filterOperationAlarms.clear()
                            filterSeverityAlarms.clear()
                        }}
                    >
                        {i18n('Clear all')}
                    </span>
                </div>
            </div>
            <CardList>
                {asset.overviewAlarms
                    .filter(el =>
                        filterSeverityAlarms.size === 0 || filterOperationAlarms.size === 0
                            ? filterOperationAlarms.size === 0 && filterSeverityAlarms.size > 0
                                ? filterSeverityAlarms.has(el.status)
                                : filterSeverityAlarms.size === 0 && filterOperationAlarms.size > 0
                                ? el.bowties_id.filter(bowtieId => filterOperationAlarms.has(bowtieId)).length > 0
                                : el
                            : filterSeverityAlarms.has(el.status) &&
                              el.bowties_id.filter(bowtieId => filterOperationAlarms.has(bowtieId)).length > 0
                    )
                    .filter(el => el.entityName?.toLowerCase().includes(stringFilter.toLowerCase()))
                    .sort((a, b) => {
                        if (order.order) {
                            if (a.timestamp > b.timestamp) return -1
                            if (a.timestamp < b.timestamp) return 1

                            return 0
                        } else {
                            if (a.timestamp < b.timestamp) return -1
                            if (a.timestamp > b.timestamp) return 1

                            return 0
                        }
                    })
                    .map((alarm, idx) => (
                        <AlarmCard
                            alarms={alarms}
                            type={'dashboard'}
                            key={alarm.id}
                            alarm={alarm}
                            asset={asset}
                            setSelectedBowtie={setSelectedBowtie}
                            onCardClick={() => handleAlarmData(alarm)}
                            setAlarmData={setAlarmData}
                            alarmBowties={alarmBowties}
                        />
                    ))}
            </CardList>

            {showSelectDropdown && (
                <div className="select-dropdown">
                    <label>{i18n('Operation')}</label>
                    <ReactSelect
                        options={asset?.operations
                            .map(operation => ({
                                label: operation.description,
                                value: operation.id
                            }))
                            .filter(option =>
                                (filterOperation ?? []).every(operation => operation.id !== option.value)
                            )}
                        onChange={(e: Option<number>) => {
                            const selectedOperation = asset?.operations?.find(operation => operation.id === e.value)

                            const newOperationFilter: IOperationData = {
                                id: selectedOperation.id,
                                description: selectedOperation.description
                            }

                            setFilterOperation([...filterOperation, newOperationFilter])
                            selectedOperation.bowtieViews.map(bowtieView =>
                                filterOperationAlarms.add(bowtieView.bowtie.id)
                            )
                        }}
                    />
                    <label>{i18n('Severity')}</label>
                    <ReactSelect
                        options={severityMock
                            .map(severity => ({ label: i18n(severity.name), value: severity.id }))
                            .filter(option => (severityFilter ?? []).every(severity => severity.id !== option.value))}
                        onChange={(e: Option<number>) => {
                            const selectedSeverity = severityMock.find(severity => severity.id === e.value)

                            const newSeverityFilter: ISeverityData = {
                                name: selectedSeverity.name,
                                id: selectedSeverity.id
                            }

                            setSeverityFilter([...severityFilter, newSeverityFilter])
                            filterSeverityAlarms.add(newSeverityFilter.name.toUpperCase())
                        }}
                    />
                </div>
            )}
        </DashboardAlarmListContainer>
    )
}
